import React, { FC, useState, useCallback } from 'react'
import debounce from 'lodash.debounce'
import { formatInTimeZone } from 'date-fns-tz'
import { format } from 'date-fns'

import Popover from '../shared/Popover'
import { AptiveIcon } from '@aptive-env/storybook'
import { fetchCalendarEvents, fetchCalendarEventParticipants } from 'services/calendar'
import { ICalendarEvent, ICalendarEventParticipant } from 'interface/calendar'
import { convert24HourTo12Hour } from 'utils/convertHour'
import { Colors } from 'constants/colors'
import EventDetailModal from 'components/EventDetailModal'

type CalendarSearchInputProps = {
  onSearch: (query: string) => void
 
}

const CalendarSearchInput: FC<CalendarSearchInputProps> = ({
  onSearch
}) => {
  const [searchValue, setSearchValue] = useState('')

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    onSearch(e.target.value)
  }

  return (
    <div className="flex items-center bg-white border border-[#E5E7EB] rounded-md h-[38px] min-w-[320px]">
      <input
        className="pl-[12px] w-full focus-visible:outline-none text-[#3d3d3d] text-sm placeholder-[#A9A9A9]"
        value={searchValue}
        onChange={onInputChange}
        placeholder="Search for events"
      />
      <div className="p-[10px]">
        <AptiveIcon className="w-[20px] h-[20px] stroke-aro-gray fill-none" icon="search" />
      </div>
    </div>
  )
}

const ResultedEvent = ({ event, onEventSuccess }: { event: ICalendarEvent, onEventSuccess: (success: string) => void }) => {

  const [isEventDetailOpened, setIsEventDetailOpened] = useState(false)
  const [isEventDetailLoading, setIsEventDetailLoading] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null)
  const [selectedEventParticipants, setSelectedEventParticipants] = useState<ICalendarEventParticipant[]>([])
  const [mapOfGroupedOverlappingEvents, setMapOfGroupedOverlappingEvents] = useState<Record<string, ICalendarEvent[][]>>({})

  const date = new Date(event.date as string)

  const handleSelect = (event: ICalendarEvent) => {
    setSelectedEvent(event)
    setIsEventDetailOpened(true)
    setIsEventDetailLoading(true)
    setSelectedEventParticipants([])
    fetchCalendarEventParticipants(event.id)
      .then((response) => {
        setSelectedEventParticipants(response)
        setIsEventDetailLoading(false)
      })
  }

  const handleHideShowEvent = (eventId: number, eventDate: Date, isAll: boolean, isHidden: boolean) => {
    const tempMapOfGroupedOverlappingEvents = {...mapOfGroupedOverlappingEvents}
    for (const date of Object.keys(tempMapOfGroupedOverlappingEvents)) {
      const tempGroupedOverlappingEvents = tempMapOfGroupedOverlappingEvents[date]
      for (const tempGroupedOverlappingEvent of tempGroupedOverlappingEvents) {
        for (const event of tempGroupedOverlappingEvent) {
          if (isAll && event.id === eventId) {
            event.is_hidden = isHidden
            break
          }
          if (!isAll && event.id === eventId && format(eventDate, 'yyyy-MM-dd') === date) {
            event.is_hidden = isHidden
            break
          }
        }
      }
    }
    setMapOfGroupedOverlappingEvents(tempMapOfGroupedOverlappingEvents)
  }

  return (
    <div>
      <div 
        className="flex p-2 gap-2 cursor-pointer hover:bg-[#F0F0F0]"
        onClick={() => handleSelect(event)}
      >
        <span className="mt-[2px] w-[12px] h-[12px] rounded-[50%] bg-aro-blue" />
        <div className="flex flex-col grow">
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-inter font-semibold text-aro-gray leading-4">
              { `${convert24HourTo12Hour(event.start_at)} - ${convert24HourTo12Hour(event.end_at)}` }
            </span>
            <span
              className="flex items-center justify-center w-[12px] h-[12px] rounded-[50%] bg-[#0EA5E926]"
            >
              <AptiveIcon
                className={'w-[8px] h-[8px] stroke-none'}
                icon="videoCamera"
                style={{ fill: Colors.ARO_BLUE }}
              />
            </span>
          </div>
          <span className="text-xs font-inter font-medium text-[#3d3d3d]">{ event.title }</span>
          <span className="text-[10px] font-inter font-medium text-aro-gray leading-4 capitalize">{ formatInTimeZone(date, 'UTC', 'LLLL, d yyyy') } - ({ event.interval })</span>
        </div>
      </div>
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          eventDate={date}
          participants={selectedEventParticipants}
          loading={isEventDetailLoading}
          open={isEventDetailOpened}
          onDelete={() => {}}
          onHideShow={handleHideShowEvent}
          onSuccess={onEventSuccess}
          onClose={() => setIsEventDetailOpened(false)}
        />
      )}
    </div>
  )
}

const CalendarSearchPopoverContent = ({ items, onEventSuccess }: { items: ICalendarEvent[], onEventSuccess: (success: string) => void }) => {
  return (
    <div className="flex flex-col w-[288px] p-[10px] max-h-[340px] overflow-auto">
      {items.map((item, index) => {
        
        return <ResultedEvent event={item} key={index} onEventSuccess={onEventSuccess} />
      })}
      {items.length === 0 && (
        <p className="text-xs text-center font-inter font-medium text-[#3d3d3d]">No events found</p>
      )}
    </div>
  )
}

const CalendarSearch = ({officeId, onEventSuccess}: { officeId: number, onEventSuccess: (success: string) => void }) => {
  const [isCalendarSearchPopoverOpen, setCalendarSearchPopoverOpen] = useState(false)
  const [searchResult, setSearchResult] = useState<ICalendarEvent[]>([])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(async (query: string) => {
    setCalendarSearchPopoverOpen(false)

    if(query && query.length > 1) {
      const eventsResult = await fetchCalendarEvents({
        office_id: officeId,
        search_text: query
      })

      const events = eventsResult.result.events
      const tempResults: ICalendarEvent[] = []
      Object.keys(events).forEach((date: string) => {
        events[date].forEach((event) => {
          tempResults.push({...event, date: date})
        })
      })

      setSearchResult(tempResults)
      setCalendarSearchPopoverOpen(true)
    } else {
      setSearchResult([])
    }
  }, 1000), [officeId])

  return (
    <Popover
      open={isCalendarSearchPopoverOpen}
      position="bottom"
      content={
        <CalendarSearchPopoverContent
          items={searchResult}
          onEventSuccess={onEventSuccess}
        />
      }
      className="right-0 rounded-[10px]"
      onClickOutside={() => setCalendarSearchPopoverOpen(false)}
    >
      <CalendarSearchInput onSearch={debouncedSearch} />
    </Popover>
  )
}

export default CalendarSearch
